import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ExportFormatCode, type SelectedClient, type SideListItem } from '@/core/models/practice.models';
import { formatMoney, type NominalLedgerReport, type TrialBalanceOption } from '@/core/models/reports.models';
import { notifyError } from '@/core/notify/notify';
import { selectClient, selectedClient } from '@/core/selectedClient';
import { practiceService } from '@/core/services/practice';
import { reportsService } from '@/core/services/reports';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Accordion, ExpansionPanel } from '@/kit/ExpansionPanel';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { Select } from '@/kit/Select';
import { FullScreenButton, useReportFullScreen } from './ReportListParts';

/** A ledger grid row: "code" shows the particular and "name" the reference, as the columns are laid out. */
interface LedgerRow {
  code: string;
  name: string;
  debit: string;
  credit: string;
}

interface LedgerPanel {
  code: string;
  name: string;
  totalDebit: string;
  totalCredit: string;
  /** BUG-053: the account's own balancing Grand Total, printed under both sides. */
  grandTotal: string;
  rows: LedgerRow[];
}

/** Panel header amounts have no currency sign and show `-` for zero, as the 3.0 does (the grid uses formatMoney). */
const headerAmount = (value: number): string =>
  value ? value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';

/**
 * Nominal Ledger report (Angular TaxreturnReportNominalladgerComponent), `/home/taxreturns/reports-ladger`: choose a
 * trial balance, Generate, view one expansion panel per nominal account, export.
 */
export function TaxreturnReportNominalladger() {
  const navigate = useNavigate();
  const [initialClient] = useState(() => selectedClient());
  const client = useRef<SelectedClient | null>(initialClient);
  const mounted = useRef(true);
  const fullScreen = useReportFullScreen();

  const [panels, setPanels] = useState<LedgerPanel[]>([]);
  /** Which panels are open: only the first after Generate; the toolbar icon opens or closes them all. */
  const [expanded, setExpanded] = useState<boolean[]>([]);
  const [generation, setGeneration] = useState(0);
  const [isExpandAll, setIsExpandAll] = useState(false);
  const [clients, setClients] = useState<SideListItem[]>([]);
  const [trialBalances, setTrialBalances] = useState<TrialBalanceOption[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(initialClient?.id ?? null);
  const [selectedTrialBalanceId, setSelectedTrialBalanceId] = useState<string | null>(null);
  const [clientName, setClientName] = useState(initialClient?.name ?? '');
  const report = useRef<NominalLedgerReport | null>(null);

  const loadTrialBalances = async () => {
    const current = client.current;
    if (!current) {
      return;
    }
    try {
      const rows = await reportsService.getTrialBalances(current.id);
      if (mounted.current) setTrialBalances(rows);
    } catch (error) {
      if (mounted.current) notifyError(error);
    }
  };

  const loadClients = async () => {
    try {
      const response = await practiceService.getClientSideList(1, 1000, '');
      if (!mounted.current) return;
      const list = response.sideModuleList;
      const current = client.current;
      if (current && !list.some((c) => c.id === current.id)) {
        list.unshift({ id: current.id, universalId: current.id, name: current.name, value: null });
      }
      setClients(list);
    } catch (error) {
      if (mounted.current) notifyError(error);
    }
  };

  useEffect(() => {
    mounted.current = true;
    if (!client.current) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return () => {
        mounted.current = false;
      };
    }
    void loadClients();
    void loadTrialBalances();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClientChange = (id: string | null) => {
    setSelectedClientId(id);
    const item = clients.find((c) => c.id === id);
    if (!item || item.id === client.current?.id) {
      return;
    }
    selectClient(item.id, item.name);
    client.current = { id: item.id, name: item.name };
    setClientName(item.name);
    setSelectedTrialBalanceId(null);
    report.current = null;
    setPanels([]);
    void loadTrialBalances();
  };

  /** Generate is running (stops a second click). */
  const [isGenerating, setIsGenerating] = useState(false);
  const generating = useRef(false);

  const generate = async () => {
    if (!selectedTrialBalanceId) {
      notifyError('Select a trial balance to generate the report.');
      return;
    }
    if (generating.current) {
      return;
    }
    generating.current = true;
    setIsGenerating(true);
    try {
      const generated = await reportsService.getNominalLedger(selectedTrialBalanceId);
      if (!mounted.current) return;
      report.current = generated;
      setClientName(generated.clientName);
      const built = generated.accounts.map((account) => ({
        code: account.code,
        name: account.name,
        totalDebit: headerAmount(account.totalDebit),
        totalCredit: headerAmount(account.totalCredit),
        grandTotal: formatMoney(account.grandTotal),
        rows: [
          ...account.entries.map((e) => ({
            code: e.particular,
            name: e.refNo,
            debit: formatMoney(e.debit),
            credit: formatMoney(e.credit),
          })),
          { code: '', name: 'Total', debit: formatMoney(account.totalDebit), credit: formatMoney(account.totalCredit) },
          {
            code: '',
            name: 'Closing Balance',
            debit: formatMoney(account.closingDebit),
            credit: formatMoney(account.closingCredit),
          },
        ],
      }));
      setPanels(built);
      setExpanded(built.map((_, index) => index === 0));
      setGeneration((n) => n + 1);
      setIsExpandAll(false);
    } catch (error) {
      if (mounted.current) notifyError(error);
    } finally {
      generating.current = false;
      if (mounted.current) setIsGenerating(false);
    }
  };

  const exportReport = async (format: ExportFormatCode) => {
    const current = report.current;
    if (!current) {
      notifyError('Generate the report before exporting it.');
      return;
    }
    try {
      await reportsService.exportNominalLedger(current.trialBalanceId, format);
    } catch (error) {
      notifyError(error);
    }
  };

  /** `toggleAccordion(); accordion.openAll()` / `closeAll()`. */
  const toggleAll = (open: boolean) => {
    setIsExpandAll((value) => !value);
    setExpanded((states) => states.map(() => open));
  };

  const columnsFor = (panel: LedgerPanel): DataTableColumn<LedgerRow>[] => [
    { id: 'code', header: 'Particular', cell: (element) => element.code, footer: ' ' },
    {
      id: 'name',
      header: 'Ref. No.',
      headerClassName: 'w-180',
      cell: (element) => element.name,
      footer: ' Grand Total ',
      footerClassName: 'text-align-right fw-bold',
    },
    {
      id: 'debit',
      header: 'Debit (£ - GBP)',
      headerClassName: 'w-180 text-align-right',
      cellClassName: 'text-align-right',
      footerClassName: 'text-align-right',
      cell: (element) => element.debit,
      footer: panel.grandTotal,
    },
    {
      id: 'credit',
      header: 'Credit (£ - GBP)',
      headerClassName: 'w-180 text-align-right',
      cellClassName: 'text-align-right',
      footerClassName: 'text-align-right',
      cell: (element) => element.credit,
      footer: panel.grandTotal,
    },
  ];

  return (
    <SideMenuPage menu="reports">
      {/* Design review 2 Nov 2026: Generate is the page's action, so it sits top-right in the sticky page header. */}
      <PageHeader title="General/Nominal Ledger Report">
        <Button className="action-button primary-button" busy={isGenerating} onClick={() => void generate()}>
          <Icon name="sync" />
          <span>Generate</span>
        </Button>
      </PageHeader>
      <div className={`content-body${fullScreen.className}`}>
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <div className="text-gray font-size-14 fw-bold">
              Select Trial Balance<span className="text-red">*</span>
            </div>
            <Select
              className="w-387"
              items={trialBalances}
              bindLabel="label"
              bindValue="id"
              placeholder="Select Trial Balance"
              value={selectedTrialBalanceId}
              onChange={(value) => setSelectedTrialBalanceId(value as string | null)}
            />
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              items={clients}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              value={selectedClientId}
              onChange={(value) => onClientChange(value as string | null)}
            />
          </div>
        </div>
        <div className="content-header pt-1 mb-0 hr-top">
          <div className="content-header-left mb-1">
            <div className="text-gray font-size-18 fw-normal">
              Generated Report for : <span className="fw-bold">{clientName}</span>
            </div>
          </div>
          <div className="content-header-right mb-1">
            <div className="toolbar">
              <Button title="Export to Excel" onClick={() => void exportReport(ExportFormatCode.Excel)}>
                <Icon name="border_all" className="icon-color-xls" />
              </Button>
              <Button title="Export to CSV" onClick={() => void exportReport(ExportFormatCode.Csv)}>
                <Icon name="description" outlined className="icon-color-xls" />
              </Button>
              <Button title="Export to PDF" onClick={() => void exportReport(ExportFormatCode.Pdf)}>
                <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
              </Button>
              <FullScreenButton fullScreen={fullScreen.fullScreen} onClick={fullScreen.toggle} />
            </div>
          </div>
        </div>

        <div className="sidenav-body wp-100 pt-10">
          <div className="sidenav-body-content p-0">
            <div className="accordion-toggle">
              {isExpandAll && <Icon name="keyboard_double_arrow_down" onClick={() => toggleAll(true)} />}
              {!isExpandAll && <Icon name="keyboard_double_arrow_up" onClick={() => toggleAll(false)} />}
            </div>
            <Accordion>
              {panels.map((panel, index) => (
                <ExpansionPanel
                  key={`${generation}-${index}`}
                  expanded={expanded[index] ?? false}
                  onExpandedChange={(open) => setExpanded((states) => states.map((s, i) => (i === index ? open : s)))}
                  title={
                    <div className="d-flex align-items-center justify-space-between wp-100">
                      <div className="flex-1 font-size-16 text-gray">
                        {panel.code} - {panel.name}
                      </div>
                      <div className="flex-1 font-size-14 text-gray d-flex align-items-center justify-content-end gap-40">
                        <div>{panel.totalDebit}</div>
                        <div>{panel.totalCredit}</div>
                      </div>
                    </div>
                  }
                >
                  <div className="sidenav-table sidenav-table-input w-auto p-1">
                    <div className="mat-table-wrapper contact-detail-table no-hover">
                      <DataTable
                        stickyHeader
                        columns={columnsFor(panel)}
                        rows={panel.rows}
                        rowKey={(_, rowIndex) => rowIndex}
                        showFooter
                      />
                    </div>
                  </div>
                </ExpansionPanel>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </SideMenuPage>
  );
}
